import React, { useState } from "react";
import { CopilotKit, useCopilotChat } from "@copilotkit/react-core";
import { CopilotSidebar, useChatContext } from "@copilotkit/react-ui";
import ChatInput from "./ChatInput";
import CustomAssistantMessage from "./CustomAssistantMessage";
import CustomUserMessage from "./CustomUserMessage";
import DraggableModal from "./DraggableModal";
import AwesomeIcon from "../AwesomeIcon";
import {
  faRobot,
  faMinus,
  faPlus,
  faXmark,
  faWindowRestore,
  faWindowMaximize,
  faStar,
  faStars,
} from "@fortawesome/pro-regular-svg-icons";
import { useStoreState } from "pullstate";
import Store, { IStore } from "../../utils/Store";
import { trackEvent } from "../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../utils/mixpanelEvents";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import NewChatModal from "./NewChatModal";
import { useAuth } from "@qure/qure-auth";

interface CopilotWrapperProps {
  children: React.ReactNode;
  authData: any;
}
const CopilotWrapper: React.FC<CopilotWrapperProps> = ({ authData, children }) => {
  const { userInfo } = useAuth();
  const isEnabled = get(userInfo, ["settings", "ui_settings", "ai_assistant"], false);

  return (
    <CopilotKit
      runtimeUrl="/api/copilotkit"
      headers={{
        "x-ai-assistant-enabled": userInfo ? String(isEnabled) : "",
      }}
      showDevConsole={false}
    >
      {children}
      <CopilotSidebar
        instructions={
          "You are assisting the user as best as you can. Answer in the best way possible given the data you have."
        }
        makeSystemMessage={defaultSystemMessage}
        Header={Header}
        Input={({ onSend, inProgress, isVisible }) => (
          <ChatInput
            inProgress={inProgress}
            isVisible={isVisible}
            onSend={onSend}
            placeholder="Ask Qure AI Assistant..."
          />
        )}
        Window={DraggableModal}
        AssistantMessage={CustomAssistantMessage}
        UserMessage={CustomUserMessage}
        labels={{
          title: "AI Assistant",
          initial:
            "Welcome to Qure AI Assistant!\nI'm your personal assistant for everything within the app. I can help you with:\n\n" +
            "Key features\n" +
            "  • Adding new patients or editing patient details\n" +
            "  • Creating and editing patient forms\n" +
            "  • Retrieving detailed patient summaries\n" +
            "  • Searching and filtering patients\n\n" +
            "Other supported features\n" +
            "  • Sharing patient information\n" +
            "  • Toggling between light and dark themes\n\n" +
            "Try slash command `/` to view ready-made prompts you can use instantly, or try Speak mode to dictate your input hands-free.",
        }}
        className="fixed bottom-0 right-0 !z-[99]"
      />
    </CopilotKit>
  );
};

function defaultSystemMessage(
  contextString: string,
  additionalInstructions?: string
): string {
  return (
    `
You are assistant, which is built by Qure to act as the AI Assistant for heathcare workers, doctors, nurse to help in managing different patient workflows according to their need

Please respond only in English. If the user inputs a message in any other language, politely inform them that you can only continue the conversation in English. Do not attempt to translate or reply in another language.

Your functionality is limited by the functions provided in the tools array, so, keep conversation limited around those tools only.
if user tries goes beyond the tools, then you can ask them to rephrase their question or answer in a way that is more likely to be handled by the tools.
If the user asks very general questions that go beyond the intended functionality or scope of the supported tools, politely inform them that your capabilities are limited to specific tools. Mention that you currently support only the tools, and can assist with tasks related to them only. Avoid answering unrelated or unsupported queries.
Help the user achieve their goals, and you do so in a way that is as efficient as possible, without unnecessary fluff, but also without sacrificing professionalism.
Always be polite and respectful, and prefer brevity over verbosity.

The user has provided you with the following context:
\`\`\`
${contextString}
\`\`\`

They have also provided you with functions you can call to initiate actions on their behalf, or functions you can call to receive more information.

Please assist them as best you can.

You can ask them for clarifying questions if needed, but don't be annoying about it. If you can reasonably 'fill in the blanks' yourself, do so.

If you would like to call a function, call it without saying anything else.
In case of a function error:
- If this error stems from incorrect function parameters or syntax, you may retry with corrected arguments.
- If the error's source is unclear or seems unrelated to your input, do not attempt further retries.
` + (additionalInstructions ? `\n\n${additionalInstructions}` : "")
  );
}

const Header = () => {
  const sortMessagesInPlace = (messages: any[]) => {
    messages.sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    const seen = new Set();
    const result: any[] = [];

    for (const message of messages) {
      if (seen.has(message.id)) continue;

      if (message.type === "ActionExecutionMessage") {
        result.push(message);
        seen.add(message.id);

        const resultMessage = messages.find(
          (m) => m.type === "ResultMessage" && m.actionExecutionId === message.id
        );
        if (resultMessage && !seen.has(resultMessage.id)) {
          result.push(resultMessage);
          seen.add(resultMessage.id);
        }
      } else {
        result.push(message);
        seen.add(message.id);
      }
    }

    // Overwrite original array with result (in-place modification)
    messages.length = 0;
    messages.push(...result);
  };

  const { labels } = useChatContext();
  const chat = useCopilotChat();
  const messages = (chat as any).visibleMessages || [];
  sortMessagesInPlace(messages);
  const setMessages = (chat as any).setMessages;
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const { isDraggableModalMaximized } = useStoreState(Store, (s: IStore) => ({
    isDraggableModalMaximized: s.isDraggableModalMaximized,
  }));
  const [isMobile, setIsMobile] = useState(false);
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      setIsMobile(window.matchMedia("(max-width: 639px)").matches);
    }
  }, []);

  const handleNewChat = () => {
    setShowNewChatModal(true);
  };

  const handleConfirmNewChat = () => {
    setMessages([]);
    setShowNewChatModal(false);
    trackEvent(MIXPANEL_EVENTS.ASSISTANT_MODAL_BUTTONS, {
      button_type: "new_chat",
      action: "click",
    });
  };

  const handleCancelNewChat = () => {
    setShowNewChatModal(false);
  };

  return (
    <>
      <div className="drag-handle cursor-move flex items-center bg-gray-700 p-4 border-b dark:bg-gray-900 border-teal-800 text-white shadow-[0_4px_24px_rgba(20,50,100,0.85)]">
        <div className="flex items-center">
          <AwesomeIcon name={faStars} size="lg" className="text-white p-1" />
          <span className="ml-3 text-lg">{labels.title}</span>
        </div>
        <div className="flex-1"></div>
        <div className="flex justify-end items-center">
          {messages.length > 0 && (
            <>
              <div className="group flex items-center relative">
                <button
                  onClick={handleNewChat}
                  aria-label="New Chat"
                  title="New Chat"
                  className="group flex items-center text-white hover:text-gray-200 p-2 transition-all duration-200"
                >
                  <span className="mr-2 text-base font-medium text-teal-400 whitespace-nowrap bg-white dark:bg-gray-900 px-3 py-1 rounded shadow-md border border-teal-200 dark:border-teal-700 z-10 transition-all duration-300 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0">
                    New Chat
                  </span>
                  <AwesomeIcon name={faPlus} size="sm" />
                </button>
              </div>
              <div className="h-6 w-px bg-gray-600 mx-3"></div>
            </>
          )}
          <div className="flex space-x-3">
            {!isMobile && (
              <>
                <button
                  onClick={() => {
                    Store.update((s: IStore) => {
                      s.isDraggableModalMinimized = !s.isDraggableModalMinimized;
                    });
                    trackEvent(MIXPANEL_EVENTS.ASSISTANT_MODAL_BUTTONS, {
                      button_type: "minimize",
                      action: "click",
                      is_minimized: !Store.getRawState().isDraggableModalMinimized,
                    });
                  }}
                  aria-label="Minimize"
                  title="Minimize"
                  className="text-white hover:text-teal-400 p-1 px-2 rounded-full transition-all duration-200 hover:scale-110"
                >
                  <AwesomeIcon name={faMinus} size="sm" />
                </button>
                <button
                  onClick={() => {
                    Store.update((s: IStore) => {
                      s.isDraggableModalMaximized = !s.isDraggableModalMaximized;
                    });
                    trackEvent(MIXPANEL_EVENTS.ASSISTANT_MODAL_BUTTONS, {
                      button_type: "maximize",
                      action: "click",
                      is_maximized: !isDraggableModalMaximized,
                    });
                  }}
                  aria-label="Maximize"
                  title="Maximize"
                  className="text-white hover:text-teal-400 p-1 px-2 rounded-full transition-all duration-200 hover:scale-110"
                >
                  <AwesomeIcon
                    name={isDraggableModalMaximized ? faWindowRestore : faWindowMaximize}
                    size="sm"
                  />
                </button>
              </>
            )}
            <button
              onClick={() => {
                Store.update((s: IStore) => {
                  s.isDraggableModalOpen = !s.isDraggableModalOpen;
                });
                trackEvent(MIXPANEL_EVENTS.ASSISTANT_MODAL_BUTTONS, {
                  button_type: "close",
                  action: "click",
                });
              }}
              aria-label="Close"
              title="Close"
              className="text-white hover:text-teal-400  p-1 px-2 rounded-full transition-all duration-200 hover:scale-120"
            >
              <AwesomeIcon name={faXmark} size="sm" />
            </button>
          </div>
        </div>
      </div>
      <NewChatModal
        isOpen={showNewChatModal}
        onConfirm={handleConfirmNewChat}
        onCancel={handleCancelNewChat}
      />
    </>
  );
};

export default CopilotWrapper;
